import { ProductShipmentResponse } from "../certified-products/certified-products.model";

export type { ProductShipmentResponse };

export interface Component {
    shipmentId: string;
    productId: string;
    componentId: string;
    tcId: string;
    tcAssociatedProductNo: string;
    tcProductComponentOrder: string;
    tcProductComponentNo: string;
    tcComponentDetail: string;
    tcComponentNetShippingWeight: number;
    tcComponentSupplementaryWeight: number;
    tcComponentCertifiedWeightTotal?: number;
    tcComponentNonCertifiedWeightTotal?: number;
  }
  
  // The API response is an array of Component objects.
  export type ApiGetComponentList = Component[];

  // Interface for component standard information-------------------------------------------------------------
export interface ComponentStandardInformation {
    tcComponentStandardLabelOrder: string;
    tcComponentStandardLabelGrade: string;
    tcComponentCertifiedWeight: number;
    additionalProp1: Record<string, any>;
  }
  
  // Interface for component raw material information
  export interface ComponentRawMaterialInformation {
    tcComponentRawMaterialOrder: string;
    tcComponentRawMaterialPercentage: number;
    tcComponentRawMaterialCode: string;
    additionalProp1: Record<string, any>;
  }
  
  // Interface for the individual component information object
  export interface TcComponentInformation {
    tcProductComponentOrder: string;
    tcAssociatedProductNo: string;
    tcProductComponentNo: string;
    tcComponentDetail: string;
    tcComponentInfoTrimsExcluded: string;
    tcComponentInfoAssociatedTc: string;
    tcComponentInfoFiberLength: string;
    tcComponentInfoYarnCount: string;
    tcComponentInfoFabricWeightAndConstruct: string;
    tcComponentInfoFabricWeightOfFinishedProduct: string;
    tcComponentNetShippingWeight: number;
    tcComponentSupplementaryWeight: number;
    tcComponentInfoAdditional: string;
    tcProductComponentStandardInformation: ComponentStandardInformation[];
    tcComponentRawMaterialInformation: ComponentRawMaterialInformation[];
    additionalProp1: Record<string, any>;
  }
  
  // Main interface for the API response
  export interface ApiGetAdditionalData {
    tcId: string;
    tcComponentInformation: TcComponentInformation[];
    additionalProp1: Record<string, any>;
    componentId: string;
    shipmentId: string;
    productId: string;
  }
  //-----------------------------------------------------------------------------

  export interface ComponentInfo {
    tcAssociatedProductNo: string;
    tcProductComponentOrder: string;
    tcProductComponentNo: string;
    tcComponentDetail: string;
    tcComponentInfoTrimsExcluded: string;
    tcComponentInfoAssociatedTc: string;
    tcComponentInfoFiberLength: string;
    tcComponentInfoYarnCount: string;
    tcComponentInfoFabricWeightAndConstruct: string;
    tcComponentInfoFabricWeightOfFinishedProduct: string;
    tcComponentNetShippingWeight: number | null;
    tcComponentSupplementaryWeight: number | null;
    tcComponentInfoAdditional: string;
  }
  
  export interface StandardInfo {
    tcComponentStandardLabelOrder: number|null;
    tcComponentStandardLabelGrade: string;
    tcComponentCertifiedWeight: string|null;
  }
  
  export interface RawMaterialInfo {
    tcComponentRawMaterialOrder: number|null;
    tcComponentRawMaterialPercentage: string|null;
    tcComponentRawMaterialCode: string;
    tcComponentRawMaterialIsCertified?: boolean;
    /** "Yes" / "No" for UI Certified column; set from API tcComponentRawMaterialIsCertified (true → "Yes", false → "No") */
    tcComponentRawMaterialCertified?: string;
  }
  
  export interface ApiComponentData {
    componentInfo: ComponentInfo;
    standardInfo: StandardInfo[];
    rawMaterialInfo: RawMaterialInfo[];
  }
  
  export interface MappedComponentPayload {
    tcId: string;
    tcComponentInformation: Array<{
      tcComponentCertifiedWeightTotal?: number;
      tcComponentNonCertifiedWeightTotal?: number;
      tcProductComponentOrder: string;
      tcAssociatedProductNo: string;
      tcProductComponentNo: string;
      tcComponentDetail: string;
      tcComponentInfoTrimsExcluded: string;
      tcComponentInfoAssociatedTc: string;
      tcComponentInfoFiberLength: string;
      tcComponentInfoYarnCount: string;
      tcComponentInfoFabricWeightAndConstruct: string;
      tcComponentInfoFabricWeightOfFinishedProduct: string;
      tcComponentNetShippingWeight: number | null;
      tcComponentSupplementaryWeight: number | null;
      tcComponentInfoAdditional: string;
      tcProductComponentStandardInformation: Array<{
        tcComponentStandardLabelOrder: string;
        tcComponentStandardLabelGrade: string;
        tcComponentCertifiedWeight: number;
        additionalProp1: Record<string, any>;
      }>;
      tcComponentRawMaterialInformation: Array<{
        tcComponentRawMaterialOrder: string|null;
        tcComponentRawMaterialPercentage: number|null;
        tcComponentRawMaterialCode: string;
        tcComponentRawMaterialIsCertified?: boolean;
        additionalProp1: Record<string, any>;
      }>;
      additionalProp1: Record<string, any>;
    }>;
    additionalProp1: Record<string, any>;
  }

  // export interface ProductShipmentResponse {
  //   shipmentId: string;
  //   productId: string;
  //   tcId: string;
  //   tcProductShipmentNo: string[];
  //   tcProductNo: string;
  //   tcProductCategoryCode: string;
  //   tcNetShippingWeight: number;
  //   tcProductProductionDate: string; // Format: YYYY-MM-DD
  //   tcProductSupplementaryWeight: number;
  //   tcProductOrderNo: string[];
  //   tcProductArticleNo: string;
  //   tcProductNumberOfUnits: number;
  //   tcProductUnitsMetric: string;
  //   tcProductInfoAssociatedTc: string;
  // }
  
  /**
   * Finds a shipment by its tcProductNo and returns an object containing its productId and shipmentId.
   *
   * @param data - Array of ProductShipmentResponse objects.
   * @param tcProductNo - The product number to look up.
   * @returns An object with productId and shipmentId if found, otherwise undefined.
   */
  export const getProductShipmentDataByProductNo = (
    data: any[],
    tcProductNo: string
  ): { productId: string; shipmentId: string[]; tcProductShipmentNo: string[] } | undefined => {
    const shipments = data.filter((shipment) => shipment.tcProductNo === tcProductNo);
    return shipments.length > 0
      ? { 
          productId: shipments[0].productId, 
          shipmentId: shipments.map(({shipmentId})=>shipmentId), 
          tcProductShipmentNo: shipments.map(({tcAssociatedProductShipmentNo})=>tcAssociatedProductShipmentNo[0]) 
        }
      : undefined;
  };

  /**
   * Retrieves the product category and detail codes for a given product number.
   *
   * @param data - Array of ProductShipmentResponse objects.
   * @param tcProductNo - The product number to look up.
   * @returns An object with tcProductCategoryCode and tcProductDetailCode if found, otherwise undefined.
   */
  export const getProductCategoryAndDetailCode = (
    data: ProductShipmentResponse[],
    tcProductNo: string
  ): { tcProductCategoryCode: string; tcProductDetailCode: string } | undefined => {
    const product = data.find((item) => item.tcProductNo === tcProductNo);
    return product
      ? {
          tcProductCategoryCode: product.tcProductCategoryCode || "",
          tcProductDetailCode: (product as any).tcProductDetailCode || "",
        }
      : undefined;
  };

  /**
   * Validates the 4 Certified Product fields based on PC-PD combinations when Component No. is empty.
   *
   * @param componentNo - The component number value.
   * @param productCategoryCode - The product category code.
   * @param productDetailCode - The product detail code.
   * @param fiberLength - The fiber length value from Certified Products.
   * @param yarnCount - The yarn count value from Certified Products.
   * @param fabricWeightAndConstruct - The fabric weight and construct value from Certified Products.
   * @param fabricWeightOfFinishedProduct - The fabric weight of finished product value from Certified Products.
   * @returns An object with validation status and error messages.
   */
  export const validateCertifiedProductFields = (
    componentNo: string,
    productCategoryCode: string,
    productDetailCode: string,
    fiberLength: string,
    yarnCount: string,
    fabricWeightAndConstruct: string,
    fabricWeightOfFinishedProduct: string
  ): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    const isComponentNoEmpty = (value: string): boolean => {
      if (!value || value.trim() === "") return true;
      const trimmedValue = value.trim().toLowerCase();
      const invalidValues = ['n/a', 'n\\a', 'null', 'not available', 'na', 'nil'];
      return invalidValues.some(invalid => trimmedValue === invalid);
    };

    const isValueInvalid = (value: string): boolean => {
      if (!value || value.trim() === "") return true;
      const trimmedValue = value.trim().toLowerCase();
      const invalidValues = ['n/a', 'n\\a', 'null', 'not available', 'na', 'nil'];
      return invalidValues.some(invalid => trimmedValue === invalid);
    };

    if (!isComponentNoEmpty(componentNo)) {
      return { valid: true, errors: [] };
    }

    // Validate Fiber Length for PC0032, PC0033, PC0034
    const fiberCategories = ["PC0032", "PC0033", "PC0034"];
    if (fiberCategories.includes(productCategoryCode)) {
      if (isValueInvalid(fiberLength)) {
        errors.push(`Fiber Length is required when Product Category is ${productCategoryCode} and Component No. is empty.`);
      }
    }

    // Validate Yarn Count for PC0029, PC0030, PC0031
    const yarnCategories = ["PC0029", "PC0030", "PC0031"];
    if (yarnCategories.includes(productCategoryCode)) {
      if (isValueInvalid(yarnCount)) {
        errors.push(`Yarn Count is required when Product Category is ${productCategoryCode} and Component No. is empty.`);
      }
    }

    // Validate Fabric Weight and Construct for PC0025, PC0026, PC0027, PC0028, PC0039
    const fabricCategories = ["PC0025", "PC0026", "PC0027", "PC0028", "PC0039"];
    if (fabricCategories.includes(productCategoryCode)) {
      if (isValueInvalid(fabricWeightAndConstruct)) {
        errors.push(`Fabric Weight and Construct is required when Product Category is ${productCategoryCode} and Component No. is empty.`);
      }
    }

    // Validate Fabric Weight of Finished Product for specific PC and PD codes
    const validProductCategories = [
      "PC0001", "PC0002", "PC0003", "PC0004", "PC0005",
      "PC0006", "PC0007", "PC0008", "PC0009", "PC0010",
      "PC0015", "PC0018", "PC0019", "PC0020", "PC0021"
    ];
    const validProductDetails = [
      "PD0001", "PD0002", "PD0003", "PD0004", "PD0005",
      "PD0006", "PD0007", "PD0008", "PD0009", "PD0010",
      "PD0011", "PD0012", "PD0013", "PD0014", "PD0015",
      "PD0016", "PD0017", "PD0019", "PD0020", "PD0021",
      "PD0022", "PD0023", "PD0024", "PD0026", "PD0028",
      "PD0029", "PD0042", "PD0043", "PD0044", "PD0051"
    ];
    if (validProductCategories.includes(productCategoryCode) || validProductDetails.includes(productDetailCode)) {
      if (isValueInvalid(fabricWeightOfFinishedProduct)) {
        errors.push(`Fabric Weight of Finished Product is required when Product Category is ${productCategoryCode} or Product Detail is ${productDetailCode} and Component No. is empty.`);
      }
    }

    return { valid: errors.length === 0, errors };
  };
  
  /**
 * Maps an ApiGetAdditionalData object to an ApiComponentData object.
 *
 * @param data - The input API data.
 * @returns The mapped ApiComponentData.
 */
  export const mapToApiComponentData = (data: Partial<ApiGetAdditionalData> | TcComponentInformation): any => {
    // If data.tcComponentInformation exists and has items, use its first element; otherwise, assume data is already the component object.
    const sourceComponent: TcComponentInformation =
      'tcComponentInformation' in data && Array.isArray(data.tcComponentInformation) && data.tcComponentInformation.length > 0
        ? data.tcComponentInformation[0]
        : (data as TcComponentInformation);
  
    // Map the componentInfo by converting numeric weight fields to strings.
    const componentInfo: ComponentInfo = {
      tcAssociatedProductNo: sourceComponent.tcAssociatedProductNo,
      tcProductComponentOrder: sourceComponent.tcProductComponentOrder,
      tcProductComponentNo: sourceComponent.tcProductComponentNo,
      tcComponentDetail: sourceComponent.tcComponentDetail,
      tcComponentInfoTrimsExcluded: sourceComponent.tcComponentInfoTrimsExcluded,
      tcComponentInfoAssociatedTc: sourceComponent.tcComponentInfoAssociatedTc,
      tcComponentInfoFiberLength: sourceComponent.tcComponentInfoFiberLength,
      tcComponentInfoYarnCount: sourceComponent.tcComponentInfoYarnCount,
      tcComponentInfoFabricWeightAndConstruct: sourceComponent.tcComponentInfoFabricWeightAndConstruct,
      tcComponentInfoFabricWeightOfFinishedProduct: sourceComponent.tcComponentInfoFabricWeightOfFinishedProduct,
      tcComponentNetShippingWeight: sourceComponent.tcComponentNetShippingWeight,
      tcComponentSupplementaryWeight: sourceComponent.tcComponentSupplementaryWeight,
      tcComponentInfoAdditional: sourceComponent.tcComponentInfoAdditional,
    };
  
    // Map standard info: convert order to number and certified weight to string.
    const standardInfo: StandardInfo[] = sourceComponent.tcProductComponentStandardInformation.map((item) => ({
      tcComponentStandardLabelOrder: item.tcComponentStandardLabelOrder ? Number(item.tcComponentStandardLabelOrder) : null,
      tcComponentStandardLabelGrade: item.tcComponentStandardLabelGrade,
      tcComponentCertifiedWeight:
        item.tcComponentCertifiedWeight !== null && item.tcComponentCertifiedWeight !== undefined
          ? String(item.tcComponentCertifiedWeight)
          : null,
    }));
  
    // Map raw material info: convert order and percentage appropriately. API returns tcComponentRawMaterialIsCertified (boolean); UI expects tcComponentRawMaterialCertified ("Yes"/"No").
    const rawMaterialInfo: RawMaterialInfo[] = sourceComponent.tcComponentRawMaterialInformation.map((item) => {
      const isCertified = (item as any).tcComponentRawMaterialIsCertified;
      return {
        tcComponentRawMaterialOrder: item.tcComponentRawMaterialOrder?Number(item.tcComponentRawMaterialOrder):null,
        tcComponentRawMaterialPercentage: item.tcComponentRawMaterialPercentage? String(item.tcComponentRawMaterialPercentage):null,
        tcComponentRawMaterialCode: item.tcComponentRawMaterialCode,
        tcComponentRawMaterialIsCertified: isCertified,
        tcComponentRawMaterialCertified: isCertified === true ? "Yes" : "No",
      };
    });
  
    return {
      componentInfo,
      standardInfo,
      rawMaterialInfo,
    };
  };
  